import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '@/api/client';
import { Scale, AlertTriangle, Clock, FileText, CheckCircle2, XCircle, Gavel } from 'lucide-react';
import { labelNivel } from '@/lib/labels';

interface Urgente {
  lista_id: number;
  n_lista: number;
  circ: string;
  tipo_eleccion: string;
  evento_id: number;
  fecha_observacion: string;
  fecha_limite: string;
  dias_habiles_restantes: number;
  alerta: string;
}

interface DashboardData {
  por_estado: Record<string, number>;
  subsanaciones_urgentes: Urgente[];
}

const ESTADO_META: Record<string, { label: string; cls: string; icon: any }> = {
  no_presentado: { label: 'No presentado', cls: 'bg-gray-200 text-gray-900',          icon: FileText },
  presentado:    { label: 'Presentado',    cls: 'bg-blue-200 text-blue-900',          icon: FileText },
  admitido:      { label: 'Admitido',      cls: 'bg-emerald-200 text-emerald-900',    icon: CheckCircle2 },
  observado:     { label: 'Observado',     cls: 'bg-amber-200 text-amber-900',        icon: AlertTriangle },
  subsanado:     { label: 'Subsanado',     cls: 'bg-violet-200 text-violet-900',      icon: FileText },
  inscrito:      { label: 'Inscrito',      cls: 'bg-green-300 text-green-900 font-bold', icon: CheckCircle2 },
  denegado:      { label: 'Denegado',      cls: 'bg-red-200 text-red-900',            icon: XCircle },
  apelado:       { label: 'Apelado',       cls: 'bg-orange-200 text-orange-900',      icon: Gavel },
};

const ORDEN = ['no_presentado', 'presentado', 'observado', 'subsanado', 'admitido', 'inscrito', 'denegado', 'apelado'];

export function ExpedienteJeeDashboard() {
  const { data, isLoading } = useQuery<DashboardData>({
    queryKey: ['expediente-jee-dashboard'],
    queryFn: async () => (await api.get('/expediente-jee/dashboard')).data,
    refetchInterval: 60_000,
  });

  if (isLoading) return <div className="p-6 text-sm text-gray-500">Cargando…</div>;
  if (!data) return null;

  const total = Object.values(data.por_estado).reduce((a, b) => a + b, 0);

  return (
    <div className="p-6 space-y-5 max-w-[1400px]">
      <header>
        <h1 className="text-xl font-bold text-arpp-primary inline-flex items-center gap-2">
          <Scale size={22} /> Expediente JEE · control consolidado
        </h1>
        <p className="text-xs text-gray-600">
          {total} listas con estado registrado · refresca cada 60s
        </p>
      </header>

      <section>
        <h2 className="text-xs uppercase tracking-wide text-gray-500 mb-2">Semáforo por estado</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2">
          {ORDEN.map((est) => {
            const cnt = data.por_estado[est] ?? 0;
            const meta = ESTADO_META[est] ?? { label: est, cls: 'bg-gray-100', icon: FileText };
            const Icon = meta.icon;
            return (
              <div key={est} className={`rounded p-3 border ${meta.cls}`}>
                <div className="flex items-center gap-1 text-[10px] uppercase font-medium opacity-80">
                  <Icon size={12} /> {meta.label}
                </div>
                <div className="text-2xl font-bold mt-1">{cnt}</div>
              </div>
            );
          })}
        </div>
      </section>

      <section>
        <h2 className="text-xs uppercase tracking-wide text-gray-500 mb-2 flex items-center gap-2">
          <AlertTriangle size={14} className="text-amber-700" />
          Subsanaciones urgentes · {data.subsanaciones_urgentes.length} listas observadas con plazo activo
        </h2>
        {data.subsanaciones_urgentes.length === 0 ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded p-4 text-sm text-emerald-900">
            ✓ Sin subsanaciones pendientes activas. Buen trabajo.
          </div>
        ) : (
          <div className="bg-white border rounded overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 text-[10px] uppercase">
                <tr>
                  <th className="text-left px-3 py-2">Lista</th>
                  <th className="text-left px-3 py-2">Tipo</th>
                  <th className="text-left px-3 py-2">Circ.</th>
                  <th className="text-left px-3 py-2">Observada</th>
                  <th className="text-left px-3 py-2">Plazo límite</th>
                  <th className="text-right px-3 py-2">Días hábiles</th>
                  <th className="text-center px-3 py-2">Estado</th>
                </tr>
              </thead>
              <tbody>
                {data.subsanaciones_urgentes.map((u) => {
                  const cls =
                    u.dias_habiles_restantes <= 0
                      ? 'bg-red-100 text-red-900 font-bold'
                      : u.dias_habiles_restantes <= 1
                      ? 'bg-red-50 text-red-800'
                      : u.dias_habiles_restantes <= 2
                      ? 'bg-orange-50 text-orange-800'
                      : 'bg-amber-50 text-amber-800';
                  return (
                    <tr key={u.evento_id} className="border-t hover:bg-gray-50">
                      <td className="px-3 py-2 font-mono">
                        <Link to={`/listas/${u.lista_id}`} className="text-arpp-primary hover:underline">
                          #{u.n_lista}
                        </Link>
                      </td>
                      <td className="px-3 py-2">{labelNivel(u.tipo_eleccion)}</td>
                      <td className="px-3 py-2">{u.circ}</td>
                      <td className="px-3 py-2 font-mono">{u.fecha_observacion}</td>
                      <td className="px-3 py-2 font-mono">{u.fecha_limite}</td>
                      <td className={`px-3 py-2 text-right ${cls}`}>
                        {u.dias_habiles_restantes <= 0 ? 'VENCIDO' : `${u.dias_habiles_restantes} día(s)`}
                      </td>
                      <td className="px-3 py-2 text-center text-lg">{u.alerta}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="bg-blue-50 border border-blue-200 rounded p-3 text-xs text-blue-900">
        <div className="flex items-start gap-2">
          <Clock size={14} className="mt-0.5" />
          <div>
            <strong>Días hábiles peruanos.</strong> Los plazos descuentan sábados, domingos y feriados nacionales
            2026 (15 feriados configurados, incluye Bicentenario 06-ago). El cómputo arranca al día siguiente de
            la notificación, según convención JEE/JNE.
          </div>
        </div>
      </section>
    </div>
  );
}
